import PropTypes from 'prop-types';
import { stringify } from 'qs';
import { useMemo } from 'react';
import styled from 'styled-components';

import { Dropdown } from '@hivebrite/design-system/Dropdown';
import { linkEffect } from '@hivebrite/design-system/Theme';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import useEventDisplayTimeZone from 'hb-react/shared/components/EventDate/hooks/useEventDisplayTimeZone';
import {
  isMobileApp,
  prefixMobileExternalLink,
} from 'hb-react/shared/utils/mobile';
import { Android } from 'helpers/mobileDeviceFromUserAgent';

const DropdownWrapper = ({ dropdownItems, dropdownPosition }) => (
  <Dropdown
    dataTestId="add-to-calendar-dropdown"
    items={dropdownItems}
    position={dropdownPosition}
    buttonWrapperProps={{ px: 0 }}
    onClickItem={(item) => window.open(item.url, '_blank')}
  >
    <LinkStyle as="span">{I18n.t('actions.add_to_calendar')}</LinkStyle>
  </Dropdown>
);

const AddToCalendar = ({
  id,
  dropdownPosition = 'right',
  timezone: eventTimezone,
  eventType,
}) => {
  const { currentUser } = useDefaultAppContexts();
  const eventDisplayTimeZone = useEventDisplayTimeZone(eventTimezone);

  const dropdownItems = useMemo(() => {
    const calendarUrl = ({ provider, format, timezone } = {}) =>
      [
        window.location.origin,
        eventType === 'event'
          ? Routes.calendar_user_event_path(currentUser.id, id)
          : Routes.calendar_event_path(id),
        stringify({ provider, format, timezone }, { addQueryPrefix: true }),
      ].join('');

    return [
      {
        label: 'Google',
        url: calendarUrl({
          provider: 'google',
          timezone: eventDisplayTimeZone,
        }),
      },
      {
        label: 'Outlook',
        url: calendarUrl({ timezone: eventDisplayTimeZone }),
      },
      {
        label: 'Yahoo',
        url: calendarUrl({ provider: 'yahoo', timezone: eventDisplayTimeZone }),
      },
      Android()
        ? null
        : {
            label: 'iCal',
            url: calendarUrl({ timezone: eventDisplayTimeZone }),
          },
    ]
      .filter(Boolean)
      .map((item) =>
        isMobileApp()
          ? { ...item, url: prefixMobileExternalLink(item.url) }
          : item,
      );
  }, [id]);

  return (
    <DropdownWrapper
      dropdownPosition={dropdownPosition}
      dropdownItems={dropdownItems}
    />
  );
};

AddToCalendar.propTypes = {
  id: PropTypes.number.isRequired,
  timezone: PropTypes.string.isRequired,
  dropdownPosition: PropTypes.string,
};

export default AddToCalendar;

const LinkStyle = styled('span')`
  ${linkEffect}
`;
